/**
 * 左の項目立て（＝管理画面の目次）。
 *
 * **日々お使いになる順**に並べ、押せる項目だけを出します（決定21・7月の提案資料のモック）。
 * まだ作っていない画面の「準備中」や、別の画面の中で行う操作の案内だけの項目は出しません
 * （押せない項目と説明が並ぶと、どこを押せばよいか分からなくなるため・総点検 J-65・J-98・M5）。
 * 「アカウントを無効にする」などは、それぞれの画面の説明に書いてあります。
 *
 * 見える・見えないは役職名では決めず、**「できることの束」**で決める（docs/01 5-4）。
 * 束の中身はサーバ（`GET /org/roles`）が返すものが正で、画面はその写しを見るだけ。
 *
 * 「管理の仕事にあたるできること」の一覧も、同じ窓口の `management_capabilities` を
 * そのまま使う。**画面側に同じ一覧を持たない**（持つと片方だけ直したときに食い違う・issue #159）。
 */

/** 表示に必要な「できること」。`admin` は管理の束を1つでも持っている方。 */
export type MenuRequirement = string | 'admin';

/** 件数の赤丸の種類（ダッシュボードと同じ窓口の数字を出す）。 */
export type MenuBadge = 'ai_pending' | 'ai_unanswered' | 'join_requests';

export interface MenuItem {
  /** 画面に出す名前（運用される方の言葉） */
  label: string;
  /** 表示に必要な「できること」 */
  requires: MenuRequirement;
  /** 受け持ちの範囲が全社の方だけに見せる項目 */
  orgScopeOnly?: boolean;
  hqOnly?: boolean;
  /** 検証のサーバ（認証番号の控えがあるサーバ）だけに出す項目（issue #342） */
  verificationOnly?: boolean;
  /** 画面の場所 */
  path: string;
  /**
   * この場所と完全に同じときだけ「いま開いているページ」にする（入口の `/` 用）。
   * 付けないと、`/rooms/新しい部屋` を開いたときに `/rooms` も光る（下の画面を開いても印が残る）。
   */
  end?: boolean;
  /** 件数の赤丸（対応を待っているものの数） */
  badge?: MenuBadge;
}

export interface MenuSection {
  /** まとまりの見出し。入口（ダッシュボード）だけは見出しを付けない */
  title?: string;
  items: MenuItem[];
}

export const MENU_SECTIONS: readonly MenuSection[] = [
  {
    items: [{ label: 'ダッシュボード', requires: 'admin', path: '/', end: true }],
  },
  {
    // AI先輩は、承認待ち → 答えられなかった質問 → 答えの一覧、と**日々触る順**に並べる
    title: 'AI先輩',
    items: [
      {
        label: '承認待ち',
        requires: 'ai_answer.approve',
        path: '/ai/pending',
        badge: 'ai_pending',
      },
      {
        label: '答えられなかった質問',
        requires: 'ai_answer.approve',
        path: '/ai/unanswered',
        badge: 'ai_unanswered',
      },
      { label: 'AI先輩の答え', requires: 'ai_answer.approve', path: '/ai/knowledge' },
      { label: '利用状況', requires: 'ai_answer.approve', path: '/ai/usage' },
      { label: '答えない領域', requires: 'ai_answer.approve', path: '/ai/restricted-topics' },
    ],
  },
  {
    title: 'トーク・お知らせ',
    items: [
      {
        label: '自動メッセージの受け口',
        requires: 'settings.manage',
        orgScopeOnly: true,
        hqOnly: true,
        path: '/intakes',
      },
      { label: 'トーク', requires: 'channel.manage_any', path: '/rooms' },
      // 休眠の棚卸し（トーク・issue #96）。自動では何も止まらない。確かめて、まとめて休眠にする
      {
        label: '長く使われていないトーク',
        requires: 'channel.manage_any',
        path: '/dormancy/rooms',
      },
      { label: 'お知らせ', requires: 'announcement.post', path: '/notices' },
      { label: 'スタンプ', requires: 'sticker.manage', path: '/stamps' },
    ],
  },
  {
    title: 'メンバー・権限',
    items: [
      // 名簿に無い方からの参加の申し込み（決定34）。承認を待つものがあれば赤丸で知らせる
      {
        label: '参加の申し込み',
        requires: 'member.manage',
        path: '/join-requests',
        badge: 'join_requests',
      },
      { label: 'メンバー', requires: 'member.manage', path: '/members' },
      // 休眠の棚卸し（人・issue #96）。自動では何も止まらない。確かめて、まとめて無効にする
      { label: '長くお使いでない方', requires: 'member.manage', path: '/dormancy/members' },
      {
        label: 'しばらく開いていない応援の方',
        requires: 'channel.manage_any',
        path: '/support/inactive',
      },
      // 一括の取り込みは会社全体の名簿を入れ替える操作なので、専用の「できること」で判定する
      // （既定では本部管理者のみ・docs/01 15-1）。サーバ側の判定と同じ束を見る
      { label: 'CSVでまとめて登録', requires: 'roster.manage', path: '/roster/import' },
      { label: '取り込みの記録', requires: 'roster.manage', path: '/roster/imports' },
      { label: '役職とできること', requires: 'admin', path: '/roles' },
      // 電話番号でのログインの上限（#336）。引き上げは本部管理者だけ（サーバも本部かを確かめる）
      {
        label: 'ログインの上限',
        requires: 'member.manage',
        orgScopeOnly: true,
        path: '/login-limits',
      },
    ],
  },
  {
    // 会社ごとの決まり（issue #124）。初期値では本部管理者だけ（サーバも同じ束で判定する）
    title: '会社の設定',
    items: [
      { label: '会社の設定', requires: 'settings.manage', path: '/settings' },
      { label: '拠点', requires: 'settings.manage', path: '/sites' },
    ],
  },
  {
    title: '記録',
    items: [
      // 月ごとの利用状況（issue #115）。拠点管理者には自拠点の分だけ（サーバが数える範囲を決める）
      { label: '月ごとの利用状況', requires: 'member.manage', path: '/usage' },
      { label: '操作履歴', requires: 'audit_log.view', path: '/audit-logs' },
      // 会社のデータの一括の取り出し（issue #114・docs/01 16-4）。受け持ちが全社の方だけ（サーバも同じ線で断る）
      {
        label: 'データの取り出し',
        requires: 'data.export_all',
        orgScopeOnly: true,
        path: '/data-export',
      },
      // 検証のサーバだけ（SMS の事業者とつなぐ前）。本番のサーバでは出ない（issue #342）
      {
        label: '認証番号の控え',
        requires: 'roster.manage',
        orgScopeOnly: true,
        verificationOnly: true,
        path: '/verification-sms',
      },
    ],
  },
];
